import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { queryClient } from './api/queryClient';
import { useUIStore } from './store/uiStore';
import { useAuthStore } from './store/authStore';
import { storage } from './lib/storage';
import App from './App';
import './styles/globals.css';

// Vite 빌드 base 를 라우터/SW 와 동기화 (prod=/, stage=/sisegonggan-dev/)
const BASE_URL = import.meta.env.BASE_URL; // 항상 끝 슬래시 포함
const BASENAME = BASE_URL.replace(/\/$/, ''); // 라우터 basename (끝 슬래시 제거)

// "브라우저 종료 시 로그아웃" — pagehide 에서 토큰/상태 초기화
window.addEventListener('pagehide', (e) => {
  if (e.persisted) return; // bfcache 복원이면 skip
  if (useUIStore.getState().logoutOnClose) {
    storage.remove('accessToken');
    useAuthStore.getState().logout();
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={BASENAME}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
);

// Service Worker 등록 (PWA + 푸시 알림)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`${BASE_URL}sw.js`, { scope: BASE_URL })
      .catch((err) => console.warn('[SW] 등록 실패:', err));
  });

  // 알림 클릭 → 해당 페이지로 SPA 이동
  navigator.serviceWorker.addEventListener('message', (e) => {
    if (e.data?.type === 'notif-click' && e.data.link) {
      // 알림 link 에 포함된 prod basename(/sisegonggan) 을 현재 basename 으로 변환
      const path = e.data.link.replace(/^\/sisegonggan(?:-dev)?/, '');
      window.history.pushState({}, '', `${BASENAME}${path}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  });

  /* 새 Service Worker 활성화 감지 → 자동 새로고침.
     sw.js 가 self.skipWaiting() + clients.claim() 호출하므로 새 SW 가 즉시 controller 가 됨.
     단일 reload 보장(swRefreshing 가드) — 중복 reload 방지. */
  let swRefreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (swRefreshing) return;
    swRefreshing = true;
    window.location.reload();
  });
}

/* 동적 청크 로드 실패 자동 복구 — 모바일 백그라운드 장시간 후 복귀 시 흔히 발생.
   새 배포로 청크 해시가 바뀌었는데 캐시 만료 전이면 404 → 화면 빈 채로 멈춤.
   sessionStorage 로 같은 세션에서 무한 reload 루프 방지 (한 번만 시도, 실패 시 그대로 노출). */
const CHUNK_RELOAD_KEY = 'sg-chunk-reload-at';
function tryRecoverChunkError(reason: unknown): boolean {
  const msg = String((reason as { message?: string })?.message ?? reason ?? '');
  const isChunkErr =
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Importing a module script failed');
  if (!isChunkErr) return false;
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  // 최근 30초 내 이미 reload 한 적 있으면 (실패하면 무한 루프 위험) skip
  if (Date.now() - last < 30_000) return false;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
  return true;
}
window.addEventListener('error', (e) => {
  void tryRecoverChunkError(e.error ?? e.message);
});
window.addEventListener('unhandledrejection', (e) => {
  void tryRecoverChunkError(e.reason);
});
